import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AircraftsAPI, Aircraft, AircraftCategory } from '../services/aircrafts.ts';
import { applyCurrencyMask, parseCurrency, formatCurrency } from '../utils/currency.ts';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import FileUpload from '../components/FileUpload.tsx';
import api from '../services/api.ts';

const EditAircraft: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: aircraft, isLoading } = useQuery(['aircraft', id], () => AircraftsAPI.get(Number(id)), { enabled: !!id });

  const [form, setForm] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    tailNumber: '',
    color: '',
    category: 'airplane' as AircraftCategory,
    purchasePriceDisplay: '',
    sellingPriceDisplay: '',
    notes: '' as string,
    attachments: [] as any[],
  });

  React.useEffect(() => {
    if (aircraft) {
      setForm({
        make: aircraft.make,
        model: aircraft.model,
        year: aircraft.year,
        tailNumber: aircraft.tailNumber,
        color: aircraft.color,
        category: (aircraft.category as AircraftCategory) || 'airplane',
        purchasePriceDisplay: formatCurrency(Number((aircraft as any).purchasePrice || 0)),
        sellingPriceDisplay: formatCurrency(Number((aircraft as any).sellingPrice || 0)),
        notes: String((aircraft as any).notes || ''),
        attachments: (aircraft as any).attachments || [],
      });
    }
  }, [aircraft]);

  const mutation = useMutation(
    async () => {
      let uploadedFiles = form.attachments || [];
      const newFiles = uploadedFiles.filter((file: any) => file.file instanceof File);
      const existingFiles = uploadedFiles.filter((file: any) => !(file.file instanceof File));

      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach((attachment: any) => {
          formData.append('files', attachment.file);
        });

        const uploadResponse = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (uploadResponse.data.success) {
          const newUploadedFiles = uploadResponse.data.data.files.map((file: any, index: number) => ({
            id: Date.now() + index,
            name: file.originalName,
            filename: file.filename,
            url: file.url,
            size: file.size,
            mimetype: file.mimetype,
            type: newFiles[index].type,
            category: newFiles[index].category,
          }));
          uploadedFiles = [...existingFiles, ...newUploadedFiles];
        }
      } else {
        uploadedFiles = existingFiles;
      }

      const payload: Partial<Aircraft> = {
        make: form.make,
        model: form.model,
        year: form.year,
        tailNumber: form.tailNumber,
        color: form.color,
        category: form.category,
        notes: form.notes || undefined,
        attachments: uploadedFiles,
      } as any;
      const purchasePrice = parseCurrency(form.purchasePriceDisplay);
      const sellingPrice = parseCurrency(form.sellingPriceDisplay);
      if (purchasePrice) (payload as any).purchasePrice = purchasePrice;
      (payload as any).sellingPrice = sellingPrice || null;
      return AircraftsAPI.update(Number(id), payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['aircraft', id]);
        queryClient.invalidateQueries(['aircrafts']);
        navigate(`/aircrafts/${id}`);
      },
    }
  );

  const handleChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCurrencyDisplay = (field: 'purchasePriceDisplay' | 'sellingPriceDisplay', value: string) => {
    handleChange(field, applyCurrencyMask(value));
  };

  if (isLoading || !aircraft) {
    return <div className="p-6">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button onClick={() => navigate(`/aircrafts/${id}`)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors">
            <ArrowLeftIcon className="h-5 w-5 mr-2" /> Voltar para Detalhes
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Editar Aeronave</h1>
        </div>

        <form onSubmit={e => { e.preventDefault(); mutation.mutate(); }} className="space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" /> Informações
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                <select
                  value={form.category}
                  onChange={e => handleChange('category', e.target.value as AircraftCategory)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                >
                  <option value="airplane">Avião</option>
                  <option value="helicopter">Helicóptero</option>
                  <option value="other">Outros</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
                <input type="text" value={form.make} onChange={e => handleChange('make', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
                <input type="text" value={form.model} onChange={e => handleChange('model', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ano</label>
                <input type="number" value={form.year} onChange={e => handleChange('year', Number(e.target.value))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prefixo</label>
                <input type="text" value={form.tailNumber} onChange={e => handleChange('tailNumber', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
                <input type="text" value={form.color} onChange={e => handleChange('color', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preço de Compra</label>
                <input type="text" value={form.purchasePriceDisplay} onChange={e => handleCurrencyDisplay('purchasePriceDisplay', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300" placeholder="R$ 0,00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preço de Venda</label>
                <input type="text" value={form.sellingPriceDisplay} onChange={e => handleCurrencyDisplay('sellingPriceDisplay', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300" placeholder="R$ 0,00" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Observação</label>
                <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300" placeholder="Anotações sobre a aeronave" />
              </div>
            </div>
          </div>

          {/* Anexos - Fotos e Documentos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fotos e Documentos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adicione fotos da aeronave e documentos como certificado de aeronavegabilidade, manual, etc.
            </p>
            <FileUpload
              files={form.attachments}
              onFilesChange={files => setForm(prev => ({ ...prev, attachments: files }))}
              maxFiles={20}
              maxSizePerFile={10}
              showCategories={true}
            />
          </div>

          <div className="flex justify-end">
            <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors">Salvar Alterações</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAircraft;
